import { useMemo, useState } from 'react';
import { TbCloudUpload } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { errorMessageFor } from '../../auth/error-message';
import { ApiError } from '../../api/api-error';
import { formatMoney } from '../../lib/format';
import { importPaymentHistoryApi as defaultApi, MAX_PAYMENT_UPLOAD_BYTES } from './import-payment-history-api';
import './import.css';

const STEPS = [
  { id: 'upload', label: 'Subir archivo' },
  { id: 'mapping', label: 'Mapear columnas' },
  { id: 'preview', label: 'Vista previa' },
  { id: 'confirm', label: 'Confirmar' },
];
const FIELDS = [
  { key: 'member', label: 'Socio', required: true },
  { key: 'email', label: 'Email' },
  { key: 'amount', label: 'Monto', required: true },
  { key: 'date', label: 'Fecha', required: true },
  { key: 'detail', label: 'Detalle' },
  { key: 'paymentBox', label: 'Caja', required: true },
  { key: 'category', label: 'Categoría', required: true },
];
const STATUS_LABEL = { create: 'A crear', created: 'Importado', error: 'Error', omitted: 'Omitida', warning: 'Advertencia' };
const STATUS_CLASS = {
  create: 'gym-badge gym-badge--success', created: 'gym-badge gym-badge--success',
  error: 'gym-badge gym-badge--danger', omitted: 'gym-badge gym-badge--neutral', warning: 'gym-badge gym-badge--info',
};
const FILTERS = [
  { value: 'all', label: 'Todas', field: null },
  { value: 'create', label: 'A crear', field: 'toCreate' },
  { value: 'error', label: 'Errores', field: 'errors' },
  { value: 'omitted', label: 'Omitidas', field: 'omitted' },
  { value: 'warning', label: 'Advertencias', field: 'warnings' },
];

function classifyError(error) {
  return error instanceof ApiError && error.status === 409 && error.code === 'import_already_committed'
    ? 'already_committed'
    : 'other';
}

function summaryCards(summary, result = false) {
  const created = result ? summary.created : summary.toCreate;
  return (
    <dl className="gym-metrics gym-import-preview__summary" data-testid="payment-import-summary">
      <div className="gym-metric-card gym-import-metric-card gym-import-metric-card--create"><dt className="gym-metric-card__label">{result ? 'Importados' : 'A crear'}</dt><dd className="gym-metric-card__value">{created}</dd></div>
      <div className="gym-metric-card gym-import-metric-card gym-import-metric-card--error"><dt className="gym-metric-card__label">Con errores</dt><dd className="gym-metric-card__value">{summary.errors}</dd></div>
      <div className="gym-metric-card gym-import-metric-card gym-import-metric-card--warning"><dt className="gym-metric-card__label">Omitidas</dt><dd className="gym-metric-card__value">{summary.omitted}</dd></div>
      <div className="gym-metric-card gym-import-metric-card gym-import-metric-card--warning"><dt className="gym-metric-card__label">Omitidas por fecha de corte</dt><dd className="gym-metric-card__value">{summary.beforeCutoff ?? 0}</dd></div>
      <div className="gym-metric-card gym-import-metric-card gym-import-metric-card--warning"><dt className="gym-metric-card__label">Advertencias</dt><dd className="gym-metric-card__value">{summary.warnings}</dd></div>
    </dl>
  );
}

function RowsTable({ rows }) {
  return (
    <div className="gym-import-table-wrapper">
      <table className="gym-table gym-import-preview-table">
        <caption className="gym-sr-only">Filas del historial de pagos</caption>
        <thead><tr><th scope="col">Fila</th><th scope="col">Socio</th><th scope="col">Plan</th><th scope="col">Monto</th><th scope="col">Estado</th><th scope="col">Observaciones</th></tr></thead>
        <tbody>{rows.map(row => (
          <tr key={row.row}>
            <td data-label="Fila">{row.row}</td>
            <td data-label="Socio">{row.name || '—'}</td>
            <td data-label="Plan">{row.membership?.planName || '—'}</td>
            <td data-label="Monto">{row.membership ? formatMoney(row.membership.amount) : '—'}</td>
            <td data-label="Estado"><span className={STATUS_CLASS[row.status]}>{STATUS_LABEL[row.status] || row.status}</span></td>
            <td data-label="Observaciones">{row.error || row.warning || row.message || '—'}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export function ImportPaymentHistoryPage({ api = defaultApi }) {
  const { handleApiError } = useAuth();
  const [step, setStep] = useState('upload');
  const [upload, setUpload] = useState(null);
  const [mapping, setMapping] = useState({});
  const [onlyBefore, setOnlyBefore] = useState('');
  const [previewedOnlyBefore, setPreviewedOnlyBefore] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [errorKind, setErrorKind] = useState('other');
  const [fileError, setFileError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [filter, setFilter] = useState('all');

  const visibleRows = useMemo(() => {
    if (filter === 'all') return preview?.rows || [];
    return (preview?.rows || []).filter((row) => row.status === filter);
  }, [filter, preview]);

  function reportFailure(caught) {
    if (handleApiError(caught).handled) return true;
    setError(errorMessageFor(caught));
    setErrorKind(classifyError(caught));
    return false;
  }

  async function handleUpload(file) {
    if (!file) return;
    const extension = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx'].includes(extension)) { setFileError('Subí un archivo .xlsx o .csv.'); return; }
    if (file.size === 0) { setFileError('El archivo está vacío.'); return; }
    if (file.size > MAX_PAYMENT_UPLOAD_BYTES) { setFileError('El archivo supera el máximo de 5 MB.'); return; }
    setFileError(''); setError(''); setBusy(true);
    try {
      const uploaded = await api.upload(file);
      setUpload(uploaded);
      setMapping(uploaded.suggestedMapping || {});
      setPreview(null); setResult(null); setStep('mapping');
    } catch (caught) { reportFailure(caught); }
    finally { setBusy(false); }
  }

  async function handlePreview(event) {
    event.preventDefault();
    if (busy) return;
    for (const field of FIELDS.filter(item => item.required)) {
      if (!mapping[field.key]) { setError(`Elegí la columna de ${field.label.toLowerCase()}.`); return; }
    }
    setBusy(true); setError('');
    try {
      const nextPreview = await api.preview({ uploadId: upload.uploadId, mapping, ...(onlyBefore ? { onlyBefore } : {}) });
      setPreview(nextPreview); setPreviewedOnlyBefore(onlyBefore); setStep('preview');
    } catch (caught) { reportFailure(caught); }
    finally { setBusy(false); }
  }

  async function handleCutoffChange(event) {
    const value = event.target.value;
    setOnlyBefore(value);
    setBusy(true); setError('');
    try {
      const nextPreview = await api.preview({ uploadId: upload.uploadId, mapping, ...(value ? { onlyBefore: value } : {}) });
      setPreview(nextPreview); setPreviewedOnlyBefore(value);
    } catch (caught) { reportFailure(caught); }
    finally { setBusy(false); }
  }

  async function handleCommit() {
    if (busy) return;
    setBusy(true); setError(''); setErrorKind('other');
    try {
      const committed = await api.commit({ uploadId: upload.uploadId, mapping, ...(previewedOnlyBefore ? { onlyBefore: previewedOnlyBefore } : {}) });
      setResult(committed); setError('');
    } catch (caught) { reportFailure(caught); }
    finally { setBusy(false); }
  }

  async function handleGetResult() {
    setBusy(true); setError('');
    try {
      const existing = await api.getResult(upload.uploadId);
      setResult(existing); setError('');
    } catch (caught) { reportFailure(caught); }
    finally { setBusy(false); }
  }

  function restart() {
    setStep('upload'); setUpload(null); setMapping({}); setOnlyBefore(''); setPreviewedOnlyBefore(''); setPreview(null); setResult(null); setError(''); setFileError(''); setFilter('all');
  }

  const activeStep = result ? 'confirm' : step;

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header gym-import-header">
        <h1 id="gym-page-title" className="gym-page__title">Importar historial de pagos</h1>
        <Link to="/admin/socios" className="gym-admin-members-link">Volver a Socios</Link>
      </header>
      <ol className="gym-import-steps" aria-label="Progreso de la importación">
        {STEPS.map((item, index) => (
          <li key={item.id} className={[
            'gym-import-step',
            item.id === activeStep ? 'gym-import-step--active' : '',
            index < STEPS.findIndex(stepItem => stepItem.id === activeStep) ? 'gym-import-step--complete' : '',
          ].filter(Boolean).join(' ')} aria-current={item.id === activeStep ? 'step' : undefined}>
            <span className="gym-import-step__number">{index + 1}</span><span>{item.label}</span>
          </li>
        ))}
      </ol>

      {step === 'upload' && !result && (
        <div className="gym-card gym-import-upload">
          <p className="gym-muted-text">Subí el archivo de pagos históricos (.xlsx o .csv, hasta 5 MB). Los pagos se vinculan con socios ya cargados.</p>
          <div
            data-testid="payment-history-dropzone"
            className={dragOver ? 'gym-import-dropzone gym-import-dropzone--active' : 'gym-import-dropzone'}
            onDragOver={(event) => { event.preventDefault(); if (!busy) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragOver(false);
              if (!busy) handleUpload(event.dataTransfer?.files?.[0]);
            }}
          >
            <TbCloudUpload className="gym-import-dropzone__icon" aria-hidden="true" />
            <p className="gym-import-dropzone__lead">Arrastrá el archivo acá</p>
            <p className="gym-muted-text">o</p>
            <label className="gym-button gym-button--primary" htmlFor="payment-history-file">Elegir archivo</label>
            <input id="payment-history-file" className="gym-sr-only" type="file" accept=".xlsx,.csv" aria-label="Archivo de pagos" disabled={busy} onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; handleUpload(file); }} />
            <p className="gym-import-dropzone__hint gym-muted-text">Formatos aceptados: .xlsx o .csv, hasta 5 MB.</p>
          </div>
          {busy && <p role="status" className="gym-import-status">Leyendo archivo…</p>}
          {(fileError || error) && <p role="alert" className="gym-form__error">{fileError || error}</p>}
        </div>
      )}

      {step === 'mapping' && upload && !result && (
        <form className="gym-form gym-card gym-import-mapping" onSubmit={handlePreview}>
          {error && <p role="alert" className="gym-form__error">{error}</p>}
          <p className="gym-muted-text">{upload.totalRows} filas. Confirmá el mapeo sugerido o ajustá cada columna.</p>
          <div className="gym-import-mapping__grid">
            {FIELDS.map(field => (
              <div className="gym-field gym-import-mapping__field" key={field.key}>
                <label className="gym-field__label" htmlFor={`payment-map-${field.key}`}>{field.label}{field.required ? '' : ' (opcional)'}</label>
                <select className="gym-input" id={`payment-map-${field.key}`} value={mapping[field.key] || ''} disabled={busy} onChange={event => setMapping(previous => ({ ...previous, [field.key]: event.target.value || null }))}>
                  <option value="">No importar</option>
                  {upload.columns.map(column => <option key={column} value={column}>{column}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div>
            <h2 className="gym-import-mapping__sample-title">Vista de muestra</h2>
            <div className="gym-import-table-wrapper"><table className="gym-table gym-import-sample-table" aria-label="Muestra de filas del archivo">
              <thead><tr>{upload.columns.map(column => <th key={column} scope="col">{column}</th>)}</tr></thead>
              <tbody>{(upload.sampleRows || []).map((row, index) => <tr key={index}>{upload.columns.map(column => <td key={column} data-label={column}>{row[column]}</td>)}</tr>)}</tbody>
            </table></div>
          </div>
          <div className="gym-admin-form-actions">
            <button type="submit" className="gym-button gym-button--primary" disabled={busy}>Generar vista previa</button>
            <button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={restart}>Volver</button>
          </div>
        </form>
      )}

      {step === 'preview' && preview && !result && (
        <div className="gym-import-preview">
          {error && <p role="alert" className="gym-form__error">{error}</p>}
          <div className="gym-field">
            <label className="gym-field__label" htmlFor="payment-history-only-before">Importar sólo pagos anteriores a</label>
            <input
              id="payment-history-only-before"
              className="gym-input"
              type="date"
              value={onlyBefore}
              disabled={busy}
              onChange={handleCutoffChange}
            />
            <p className="gym-muted-text">El importador de socios ya cargó el período actual de cada socio con lo que pagó. Si este archivo incluye pagos de ese mismo período (por ejemplo, del mes en curso), importarlos los contaría dos veces. Elegí la fecha desde la que ya no querés importar pagos; por ejemplo, el día en que empieza el período actual más viejo.</p>
          </div>
          {summaryCards(preview.summary)}
          <div className="gym-import-preview__filters" role="group" aria-label="Filtrar por estado">
            {FILTERS.map(({ value, label, field }) => (
              <button
                key={value}
                type="button"
                className={filter === value ? 'gym-admin-filter gym-admin-filter--active' : 'gym-admin-filter'}
                aria-pressed={filter === value}
                disabled={busy}
                onClick={() => setFilter(value)}
              >
                {label}{field ? ` (${preview.summary[field]})` : ''}
              </button>
            ))}
          </div>
          <RowsTable rows={visibleRows} />
          <div className="gym-admin-form-actions">
            <button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={() => { setStep('mapping'); setError(''); }}>Volver a mapear</button>
            <button type="button" className="gym-button gym-button--primary" disabled={busy || onlyBefore !== previewedOnlyBefore} onClick={() => { setStep('confirm'); setError(''); }}>Confirmar importación</button>
          </div>
        </div>
      )}

      {step === 'confirm' && !result && (
        <div className="gym-card gym-import-confirm">
          {error && <p role="alert" className="gym-form__error">{error}</p>}
          {errorKind === 'already_committed' ? (
            <>
              <p>Esta carga ya se confirmó. Consultá el resultado guardado antes de continuar.</p>
              <div className="gym-admin-form-actions"><button type="button" className="gym-button gym-button--primary" disabled={busy} onClick={handleGetResult}>Ver resultado existente</button></div>
            </>
          ) : (
            <>
              {busy ? <p role="status" className="gym-import-status">Importando pagos… no cierres esta ventana.</p> : <p>Se procesarán {preview?.summary.toCreate || 0} filas importables. Los errores, omisiones y advertencias quedarán detallados en el resultado.</p>}
              <div className="gym-admin-form-actions">
                {!busy && <button type="button" className="gym-button gym-button--primary" onClick={handleCommit}>Confirmar e importar</button>}
                {!busy && <button type="button" className="gym-button gym-button--secondary" onClick={() => setStep('preview')}>Volver</button>}
              </div>
            </>
          )}
        </div>
      )}

      {result && (
        <div className="gym-import-preview">
          <div className="gym-card gym-import-confirm"><p className="gym-import-confirm__done-lead">Importación completada.</p>{summaryCards(result.summary, true)}</div>
          <RowsTable rows={result.rows || []} />
          <div className="gym-admin-form-actions"><button type="button" className="gym-button gym-button--secondary" onClick={restart}>Importar otro archivo</button></div>
        </div>
      )}
    </section>
  );
}
